import { addUnit } from '@mentimeter/ragnar-utils';
import type { Asset } from 'types/generated/contentful';
import React from 'react';
import { getContentfulAssetImageProps } from 'src/cms/utils/utils';
import NextImage from 'src/components/image/NextImage';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Ratio } from '@mentimeter/ragnar-ui/ratio';
import { H3 } from '../../../ui/typography';
import { ScreenshotServiceImage } from './ScreenshotServiceImage';

const TITLE_FONT_SIZE_INDEX = 3;

interface Props extends BoxT {
  featTemplateBgColor?: string;
  isPro?: boolean;
  presentationId: string;
  previewImage?: Asset | null | undefined;
  questionId: string;
  title: string;
  numberOfSlides?: number;
}

export const Featured = React.memo(
  ({
    featTemplateBgColor,
    isPro = false,
    previewImage,
    presentationId,
    questionId,
    title,
    numberOfSlides,
  }: Props) => {
    return (
      <>
        <Ratio
          customRatio={`${(900 / 1600) * 100}%`}
          width="100%"
          bg={featTemplateBgColor}
        >
          <Box
            height="100%"
            left="50%"
            position="absolute"
            top="0"
            width="70%"
            extend={() => ({
              transform: 'translateX(-50%)',
            })}
          >
            <Box
              height={0}
              left="0"
              overflow="hidden"
              position="absolute"
              pt={`${(900 / 1600) * 100}%`}
              top="50%"
              extend={({ theme }) => ({
                borderRadius: `${theme.radii[1]}px`,
                boxShadow: '0px 5px 25px 3px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-50%)',
                width: '100%',
              })}
            >
              {previewImage ? (
                <NextImage
                  alt={title}
                  {...getContentfulAssetImageProps(previewImage)}
                  width={600}
                  height={400}
                />
              ) : (
                <ScreenshotServiceImage
                  alt={title}
                  presentationId={presentationId}
                  questionId={questionId}
                  style={{
                    height: 'auto',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                  }}
                />
              )}
            </Box>
          </Box>
          {isPro && (
            <Text
              bg="positive"
              fontWeight="semiBold"
              fontSize="75"
              lineHeight="none"
              p="space1"
              extend={({ theme }) => ({
                borderRadius: `${theme.radii[1]}px`,
                position: 'absolute',
                top: '12px',
                right: '12px',
              })}
            >
              PRO
            </Text>
          )}
        </Ratio>
        <Box p={13} bg="bg" width="100%" mt="auto">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="baseline"
            width="100%"
            overflow="hidden"
          >
            <Box
              flex="1 1 auto"
              extend={() => ({
                display: '-webkit-box',
                '-webkit-box-orient': 'vertical',
                '-webkit-line-clamp': 2,
                overflow: 'hidden',
              })}
            >
              <H3
                fontSize={TITLE_FONT_SIZE_INDEX}
                color="text"
                fontWeight="semiBold"
                mb="space0"
                extend={({ theme }) => ({
                  minHeight: `calc(${addUnit(
                    theme.fontSizes[TITLE_FONT_SIZE_INDEX],
                  )} * ${theme.lineHeights.body * 2})`,
                })}
              >
                {title}
              </H3>
            </Box>
            {numberOfSlides && (
              <Text
                fontSize="100"
                color="textWeaker"
                pl="space2"
                extend={() => ({ whiteSpace: 'nowrap' })}
              >
                {numberOfSlides === 1
                  ? `${numberOfSlides} slide`
                  : `${numberOfSlides} slides`}
              </Text>
            )}
          </Box>
        </Box>
      </>
    );
  },
);
